import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { StyleContext } from "../../providers/StyleContextProvider";
import { AuthContext } from "../../providers/AuthContextProvider";
import { LoggedAdmin } from "../../models/LoggedAdmin";
import { useMediaQuery } from "react-responsive";
import { getStateDegrees, getStateFaculties, getStateUniversities } from "../../services/DegreeService";
import { Header } from "../../components/shared/Header";
import { NoData } from "../../components/shared/NoData";
import { Select } from "../../components/shared/Select";
import useErrorPage from "../../hooks/useErrorPage";
import useAdminLoginRedirect from "../../hooks/useAdminLoginRedirect";
import { Option } from "../../models/Option";
import { Button } from "../../components/shared/Button";
import { TableLoader } from "../../components/loaders/TableLoader";
import { SelectLoader } from "../../components/loaders/SelectLoader";
import { DegreeModel } from "../../models/DegreeModel";

export const StateDegreeListPage: React.FC = () => {
    const [searchParams] = useSearchParams(); 
    const navigate = useNavigate();
    const styleContext = useContext(StyleContext);
    const authContext = useContext(AuthContext);
    const [styles, setStyles] = useState<any>(styleContext.getComponentStyle("degreeListPage"));  
    const admin: LoggedAdmin | null = authContext.getLoggedAdmin();  
    const universityId: number | undefined = searchParams.get("university-id") === null ? undefined : +searchParams.get("university-id")!;
    const facultyId: number | undefined = searchParams.get("faculty-id") === null ? undefined : +searchParams.get("faculty-id")!;
    const keyword: string | undefined = searchParams.get("keyword") === null ? undefined : searchParams.get("keyword")!; 
    const [searchKeyword, setSearchKeyword] = useState<string>("");
    const [universities, setUniversities] = useState<Option[]>([]);
    const [faculties, setFaculties] = useState<Option[]>([]);
    const [degrees, setDegrees] = useState<DegreeModel[]>([]);    
    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>();   
    const [hoveredIndex, setHoveredIndex] = useState<number>(-1);
    const [loading, setLoading] = useState<boolean>(true);
    const [optionLoading, setOptionLoading] = useState<boolean>(true);
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
    const [toErrorPage] = useErrorPage();
    const [toAdminLogin] = useAdminLoginRedirect();

    useEffect(() => {
        if(admin === null) {
            toAdminLogin("Please login to access this page.");
        }
        else {
            getUniversityList();
        }
    }, []);

    useEffect(() => {
        getFacultyList();                                    
    }, [universityId]);

    useEffect(() => {
        getDegreeList();                                    
    }, [universityId, facultyId, keyword]);
    
    useEffect(() => {        
        setStyles(styleContext.getComponentStyle("degreeListPage")); 
    }, [isMobile]);    

    async function getUniversityList() {                
        await getStateUniversities(admin!.adminId, admin!.token)
        .then((universityList: Option[]) => {
            setUniversities(universityList);
            setOptionLoading(false);            
        })
        .catch((error) => {            
            toErrorPage(error);
        });                       
    }

    async function getFacultyList() {                
        setOptionLoading(true);            
        await getStateFaculties(universityId, admin!.adminId, admin!.token)
        .then((facultyList: Option[]) => {
            setFaculties(facultyList); 
            setOptionLoading(false);            
        })
        .catch((error) => {            
            toErrorPage(error);
        });                       
    }

    async function getDegreeList() {                
        setLoading(true);         
        await getStateDegrees(universityId, facultyId, keyword, admin!.adminId, admin!.token)
        .then((degreeList: DegreeModel[]) => {
            setDegrees(degreeList);  
            setLoading(false);
        })
        .catch((error) => {            
            toErrorPage(error);
        });                       
    }

    const setUniversity = (selectedUniversityId: string) => {         
        if(selectedUniversityId) {        
            searchParams.set("university-id", selectedUniversityId);
        }
        else {
            searchParams.delete("university-id");
        }
        handleNavigate();
    }
    
    const setFaculty = (selectedFacultyId: string) => {  
        if(selectedFacultyId) {
            searchParams.set("faculty-id", selectedFacultyId);
        }
        else {
            searchParams.delete("faculty-id");
        }
        handleNavigate();
    }

    const setKeyword = (keywordInput: string) => {
        if(keywordInput === "") {
            searchParams.delete("keyword");
        }   
        else {                
            searchParams.set("keyword", keywordInput);
        }
        handleNavigate();
    }
    
    const handleKeywordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();  
        const value = event.target.value;
        setSearchKeyword(value);

        // Clear previous timeout
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        // Set new timeout
        const newTimeoutId = setTimeout(() => {
            setKeyword(value.trim());
        }, 1000);
        setTimeoutId(newTimeoutId);
    };

    const handleNavigate = () => {                                    
        const newUrl = `/state-degrees?${searchParams}`;
        navigate(newUrl);                
    }

    const navigateToPrivateDegrees = () => {
        navigate("/private-degrees");
    }

    const navigateToEdit = (degreeId: number) => {
        navigate("/state-degree/edit/" + degreeId);
    }

    return (   
        <>
            <Header /> 
            <div style={styles}>                         
                <div style={styles.accordionButtonsContainer}>
                    <div style={{ ...styles.accordionButton, ...styles.accordionButton.left, ...styles.accordionButton.selected }}>
                        {"State Degrees"}
                    </div>
                    <div
                        style={{ ...styles.accordionButton, ...styles.accordionButton.right }}
                        onClick={navigateToPrivateDegrees} 
                    >
                        {"Private Degrees"}
                    </div>
                </div> 
                {optionLoading ? <SelectLoader count={3} />  :      
                <div style={styles.searchWrapper}>
                    {universities.length > 0 && <Select 
                        styles={styles.searchSelect}
                        label={"Universities"} 
                        options={universities} 
                        initialValue={universityId?.toString()}
                        getSelectedValue={setUniversity} 
                    />}
                    {faculties.length > 0 && <Select 
                        styles={styles.searchSelect}
                        label={"Faculties"} 
                        options={faculties} 
                        initialValue={facultyId?.toString()}
                        getSelectedValue={setFaculty} 
                    />}                    
                    <input
                        type={"text"}
                        placeholder="Search by keywords"
                        value={searchKeyword}
                        onChange={(e) => handleKeywordChange(e)} 
                        style={styles.searchInput}                                                                                    
                    />
                </div>}
                {loading ? <TableLoader columnCount={5} rowCount={10} /> : 
                <div>                
                    {(degrees.length === 0) ? <NoData/> :
                    <table style={styles.table}>
                        <thead>
                            <tr style={styles.table.header}> 
                                <th style={styles.table.idColumn}>{"Id"}</th>                           
                                <th style={styles.table.universityColumn}>{"University"}</th>
                                <th style={styles.table.facultyColumn}>{"Faculty"}</th> 
                                <th style={styles.table.degreeColumn}>{"Degree"}</th>
                                <th style={styles.table.periodColumn}>{"Period"}</th>                               
                            </tr>
                        </thead>
                        <tbody>
                            {degrees.map((degree, index) => (
                                <tr 
                                    key={index} 
                                    style={{...styles.table.tr, ...(index === hoveredIndex && styles.table.hoveredTr)}}
                                    onMouseEnter={() => setHoveredIndex(index)}
                                    onMouseLeave={() => setHoveredIndex(-1)}
                                >
                                    <td>{degree.degreeId}</td>
                                    <td>
                                        <div style={styles.table.rowWrapper}>
                                            <div>
                                                <img src={degree.universityLogoUrl} style={styles.table.logo}></img>
                                            </div>
                                            <div>
                                                {degree.universityName}<br/>
                                                {degree.universityName_ENG}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        {degree.facultyName}<br/>
                                        {degree.facultyName_ENG}
                                    </td>
                                    <td>
                                        {degree.degreeName}<br/>
                                        {degree.degreeName_ENG}                                        
                                    </td>
                                    <td style={styles.table.periodCell}>
                                        <div>
                                            {degree.degreePeriodName}<br/>
                                            {degree.degreePeriodName_ENG}
                                        </div>
                                        {index === hoveredIndex && <Button style={styles.table.editButton} onClick={() => navigateToEdit(degree.degreeId)}>Edit</Button>}
                                    </td>                                                                       
                                </tr>
                            ))}
                        </tbody>
                    </table>}                    
                </div>}
            </div>
        </>         
    )
}